/**
 * Default Brief Wizard Config — Per-Region Architecture
 *
 * Each brief JSON targets ONE region (or none). The `region_key` and
 * `region_label` fields identify the region; `regionPromptFragment`
 * provides the descriptive text injected into the prompt via the
 * `{{_region_context}}` placeholder.
 *
 * Region-free briefs (e.g. SaaS screenshots) leave `region_key` null
 * and `regionPromptFragment` empty.
 *
 * The old "region_picker" step has been removed — region is metadata,
 * not a wizard step.
 */

export const defaultWizardConfig = {
  version: "1.0",

  // ── Region Metadata ────────────────────────────────────────────────
  // Set these when creating a brief for a specific region.
  // Leave region_key null and regionPromptFragment "" for region-free briefs.
  region_key: "nordics_no",
  region_label: "Norway",
  regionPromptFragment:
    "A Norwegian home setting — timber-clad walls, muted earth tones, fjord-inspired natural light, birch and pine details",

  steps: [
    {
      id: "context",
      title: "Domestic Context",
      subtitle: "What kind of home and room?",
      icon: "home",
      type: "grouped_select",
      fields: [
        {
          key: "zone_key",
          label: "Zone",
          options: [
            { key: "interior_living_space", label: "Interior / Living Space", description: "Main social areas — living room, dining room, open-plan kitchen", emoji: "🛋️" },
            { key: "interior_bedroom", label: "Interior / Bedroom", description: "Private spaces — master bedroom, children's rooms, guest rooms", emoji: "🛏️" },
            { key: "exterior_facade", label: "Exterior / Facade", description: "Building exterior — front entrance, walls, architectural features", emoji: "🏠" },
            { key: "exterior_garden", label: "Exterior / Garden", description: "Outdoor living — patio, deck, garden areas, poolside", emoji: "🌿" },
            { key: "utility", label: "Utility", description: "Functional spaces — garage, laundry, basement, technical rooms", emoji: "🔧" },
          ],
        },
        {
          key: "household_type",
          label: "Home Type",
          options: [
            { key: "apartment", label: "Apartment", description: "Urban flat, typically compact with modern finishes" },
            { key: "family_home", label: "Family Home", description: "Detached house with garden, typical suburban feel" },
            { key: "suburban_house", label: "Suburban House", description: "Row house or semi-detached, residential neighborhood" },
            { key: "modern_villa", label: "Modern Villa", description: "High-end contemporary design, open spaces, premium materials" },
            { key: "traditional_home", label: "Traditional Home", description: "Classic architecture, wood paneling, historical character" },
          ],
          conditionalOptions: {
            zone_utility: [
              { key: "garage", label: "Garage", description: "Car storage, workbench area, utility wall space" },
              { key: "shed", label: "Shed / Outbuilding", description: "Garden shed or detached storage building" },
              { key: "laundry_room", label: "Laundry Room", description: "Dedicated washing area with utility connections" },
              { key: "basement", label: "Basement", description: "Below-grade space, often concrete or unfinished walls" },
              { key: "boiler_room", label: "Boiler / Technical Room", description: "HVAC, water heater, electrical panel area" },
              { key: "mudroom", label: "Mudroom / Entrance", description: "Transition space between outdoors and living area" },
            ],
            // Region-specific home types — these always apply since this brief IS for Norway.
            // When duplicating this brief for another region, replace these with region-appropriate options.
            region_nordics_no: [
              { key: "mountain_cabin", label: "Mountain Cabin (Fjellhytte)", description: "Alpine cabin with timber, stone fireplace, mountain views" },
              { key: "log_cabin", label: "Log Cabin (Laftehytte)", description: "Traditional round-log construction, rustic and warm" },
              { key: "sea_cabin", label: "Sea Cabin (Sjøhytte)", description: "Coastal cabin, weathered wood, ocean panoramas" },
              { key: "forest_cabin", label: "Forest Cabin (Skogshytte)", description: "Woodland retreat surrounded by pine and birch" },
            ],
          },
        },
      ],
    },
    {
      id: "time",
      title: "Time & Season",
      subtitle: "Set the lighting and atmosphere",
      icon: "sun",
      type: "dual_select",
      fields: [
        {
          key: "season",
          label: "Season",
          options: [
            { key: "spring", label: "Spring", description: "Fresh greens, blossoms, soft natural light", emoji: "🌸" },
            { key: "summer", label: "Summer", description: "Bright daylight, warm tones, lush vegetation", emoji: "☀️" },
            { key: "autumn", label: "Autumn", description: "Golden/amber palette, falling leaves, cozy atmosphere", emoji: "🍂" },
            { key: "winter", label: "Winter", description: "Cool blues, snow, warm interior glow contrast", emoji: "❄️" },
          ],
        },
        {
          key: "time_of_day",
          label: "Time of Day",
          options: [
            { key: "morning", label: "Morning", description: "Soft warm light, long shadows, golden hour feel", emoji: "🌅" },
            { key: "midday", label: "Midday", description: "Bright even lighting, minimal shadows, clear", emoji: "☀️" },
            { key: "afternoon", label: "Afternoon", description: "Warm angled light, gentle shadows, relaxed feel", emoji: "🌤️" },
            { key: "evening", label: "Evening", description: "Warm interior lighting, blue-hour exterior glow", emoji: "🌆" },
            { key: "night", label: "Night", description: "Artificial lighting focus, dramatic contrast, intimate", emoji: "🌙" },
          ],
        },
      ],
    },
    {
      id: "presence",
      title: "Human Presence",
      subtitle: "People in the scene",
      icon: "users",
      type: "grouped_select",
      fields: [
        {
          key: "presence_level",
          label: "Presence Level",
          options: [
            { key: "none", label: "None", description: "Empty scene, no signs of inhabitants — product showcase focus" },
            { key: "implied", label: "Implied", description: "Signs of life without visible people — a coffee cup, an open book, shoes at the door" },
            { key: "background", label: "Background", description: "People visible but not the focus — blurred, partially visible, in the distance" },
            { key: "active", label: "Active Household", description: "People interacting with the space — clear but secondary to the scene" },
          ],
        },
        {
          key: "household_type_people",
          label: "Household Type",
          showWhen: { field: "presence_level", values: ["background", "active"] },
          options: [
            { key: "family_young_kids", label: "Family with Young Children", description: "Toys visible, child-safe environment, lively atmosphere" },
            { key: "family_teens", label: "Family with Teens", description: "Tech-savvy home, mixed modern and practical decor" },
            { key: "young_couple", label: "Couple", description: "Stylish minimalism, curated decor, date-night vibes" },
            { key: "single_professional", label: "Single", description: "Clean, functional space with personal touches" },
            { key: "elderly", label: "Elderly", description: "Warm, well-lit, accessible layout, classic furnishings" },
            { key: "multi_generational", label: "Multi-Generational", description: "Diverse needs, mix of traditional and modern elements" },
          ],
        },
        {
          key: "interaction_type",
          label: "Interaction Type",
          showWhen: { field: "presence_level", values: ["background", "active"] },
          options: [
            { key: "none", label: "No specific interaction" },
            { key: "fun_interaction", label: "Fun interaction" },
            { key: "operating_product", label: "Operating product" },
            { key: "impressed", label: "Impressed with product" },
            { key: "relaxing_nearby", label: "Relaxing nearby" },
            { key: "showing_guest", label: "Showing product to guest" },
            { key: "family_moment", label: "Family moment" },
            { key: "working_from_home", label: "Working from home" },
            { key: "playing_with_kids", label: "Playing with kids" },
          ],
        },
      ],
    },
    {
      id: "mounting",
      title: "Mounting Height",
      subtitle: "Where is the product installed?",
      icon: "ruler",
      type: "tile_select",
      fieldKey: "placement_height",
      options: [
        { key: "floor", label: "Floor / Low", description: "Floor-standing or low wall mount (0–80 cm). Ground-level appliances, baseboard units." },
        { key: "mid", label: "Mid-Level", description: "Eye-level mounting (100–150 cm). Thermostats, control panels, small displays." },
        { key: "high", label: "High / Ceiling", description: "Near ceiling, ~15–20 cm gap (typical HVAC). Air conditioners, ventilation units." },
      ],
    },
    {
      id: "notes",
      title: "Additional Notes",
      subtitle: "Any extra instructions",
      icon: "message-square",
      type: "textarea",
      fieldKey: "prompt",
      placeholder: "Add any specific instructions, references, or details you want the AI to consider when generating this visual...",
    },
  ],
  constraints: {
    hvac_indoor_unit: {
      hide_options: {
        zone_key: ["exterior_facade", "exterior_garden"],
        placement_height: ["floor"],
      },
      defaults: {
        zone_key: "interior_living_space",
        placement_height: "high",
      },
    },
  },

  // ── Prompt Assembly ──────────────────────────────────────────────────
  // {{_region_context}} is replaced with `regionPromptFragment` at assembly time.
  // If the brief has no region, this placeholder resolves to an empty string.

  promptTemplate: [
    "A photorealistic interior/architectural photograph.",
    "",
    "SETTING: {{_region_context}}. {{zone_key}} of a {{household_type}}.",
    "SEASON & LIGHT: {{season}}, {{time_of_day}}.",
    "{{_photographer_direction}}",
    "",
    "PRODUCT: {{_product_description}}.",
    "PRODUCT DETAILS:",
    "{{_product_details}}",
    "PLACEMENT: The product is mounted at {{placement_height}}.",
    "",
    "REFERENCE IMAGES: I have attached reference images of the product. Use these to accurately reproduce the product's exact appearance, proportions, color, and details. The attached images show the product from the following angles: {{_reference_image_list}}.",
    "",
    "PEOPLE: {{presence_level}}.{{household_type_people}}{{interaction_type}}",
    "",
    "{{prompt}}",
  ].join("\n"),

  // Maps each field key + option key → descriptive text for the prompt.
  // Note: market_key fragments have been removed. Region context is now
  // provided by the top-level `regionPromptFragment` field.
  promptFragments: {
    zone_key: {
      interior_living_space: "The living space (living room, dining area, or open-plan kitchen)",
      interior_bedroom: "The bedroom (master, children's, or guest room)",
      exterior_facade: "The building exterior and facade",
      exterior_garden: "The outdoor garden, patio, or deck area",
      utility: "A utility or functional space",
    },
    household_type: {
      apartment: "a modern urban apartment with compact, efficient layout",
      family_home: "a detached family home with garden and suburban character",
      suburban_house: "a suburban row house or semi-detached home",
      modern_villa: "a high-end modern villa with open spaces and premium materials",
      traditional_home: "a traditional home with classic architecture and wood paneling",
      garage: "a residential garage with workbench and utility walls",
      shed: "a garden shed or detached outbuilding",
      laundry_room: "a dedicated laundry room",
      basement: "a below-grade basement space",
      boiler_room: "a boiler or technical room with HVAC equipment",
      mudroom: "a mudroom / entrance transition space",
      mountain_cabin: "a Norwegian mountain cabin (fjellhytte) with timber and stone",
      log_cabin: "a traditional Norwegian log cabin (laftehytte)",
      sea_cabin: "a Norwegian coastal cabin (sjøhytte) with weathered wood",
      forest_cabin: "a Norwegian forest cabin (skogshytte) surrounded by pine and birch",
    },
    season: {
      spring: "springtime — fresh greens, blossoms, soft diffused natural light",
      summer: "summer — bright warm daylight, lush green vegetation, vibrant colors",
      autumn: "autumn — golden and amber palette, falling leaves, cozy warm atmosphere",
      winter: "winter — cool blue tones, snow outside, warm interior glow contrast",
    },
    time_of_day: {
      morning: "morning light — soft golden warmth, long gentle shadows",
      midday: "midday — bright even lighting, minimal shadows, clear and crisp",
      afternoon: "afternoon — warm angled sunlight, gentle shadows, relaxed ambiance",
      evening: "evening — warm interior lighting, blue-hour glow from windows",
      night: "nighttime — artificial lighting, dramatic contrast, intimate atmosphere",
    },
    // Photographer-related fragments (camera_type, lens_style, etc.) are NOT here.
    // They live in the Photographer JSON and are injected via {{_photographer_direction}}.
    placement_height: {
      floor: "floor level or low wall (0–80 cm height)",
      mid: "mid-level / eye height (100–150 cm)",
      high: "high on the wall near the ceiling (~15–20 cm gap from ceiling)",
    },
    presence_level: {
      none: "No people in the scene — empty, product-focused",
      implied: "No visible people, but signs of life — a coffee cup, an open book, shoes by the door",
      background: "People visible in the background, slightly blurred, not the focus",
      active: "People actively present and interacting with the space",
    },
    household_type_people: {
      family_young_kids: " The household is a family with young children — toys visible, child-safe environment.",
      family_teens: " The household has teenagers — tech-savvy, mixed modern decor.",
      young_couple: " A couple's home — stylish minimalism, curated decor.",
      single_professional: " A single professional's space — clean, functional, personal touches.",
      elderly: " An elderly person's home — warm, well-lit, accessible layout, classic furnishings.",
      multi_generational: " A multi-generational household — diverse needs, traditional meets modern.",
    },
    interaction_type: {
      none: "",
      fun_interaction: " Someone is playfully interacting with the product.",
      operating_product: " A person is operating or adjusting the product.",
      impressed: " A person looks impressed or pleased with the product.",
      relaxing_nearby: " Someone is relaxing near the product.",
      showing_guest: " A person is showing the product to a guest.",
      family_moment: " A family moment is happening near the product.",
      working_from_home: " Someone is working from home in the scene.",
      playing_with_kids: " Children are playing in the scene.",
    },
  },
};

export type WizardConfig = typeof defaultWizardConfig;
